import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import ButtonBase from '@material-ui/core/ButtonBase';
import IconButton from '@material-ui/core/IconButton';
import InputBase from '@material-ui/core/InputBase';
import LinearProgress from '@material-ui/core/LinearProgress';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { fade, makeStyles } from '@material-ui/core/styles';
import PeopleIcon from '@material-ui/icons/People';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import green from '@material-ui/core/colors/green';
import orange from '@material-ui/core/colors/orange';
import red from '@material-ui/core/colors/red';
import JString from '@easytech-international-sdn-bhd/jstring';

const SEGMENT_MAX = 24;

const usersShownText = (template, shown, total) => {
	if (!template) return `${shown} / ${total}`;
	const first = JString(template).replaceInfinity('@shown', String(shown)).str;
	return JString(first).replaceInfinity('@total', String(total)).str;
};

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === 'dark';
	const line = fade(theme.palette.text.primary, 0.12);
	const soft = fade(theme.palette.text.primary, isDark ? 0.07 : 0.05);
	const wide = theme.breakpoints.up('md');
	const narrow = theme.breakpoints.down('sm');
	return {
		// Grid columns: [gutter] title | divider | stats | search / bar | actions / percent [gutter]
		root: {
			position: 'relative',
			overflow: 'hidden',
			marginBottom: 16,
			border: `1px solid ${theme.palette.divider}`,
			borderRadius: 16,
			background: isDark
				? 'linear-gradient(135deg, rgba(254,242,0,0.07) 0%, rgba(254,242,0,0.015) 48%, rgba(255,255,255,0.02) 100%)'
				: 'linear-gradient(135deg, rgba(254,242,0,0.16) 0%, rgba(254,242,0,0.04) 48%, rgba(255,255,255,0.9) 100%)',
			[wide]: {
				display: 'grid',
				gridTemplateColumns: '4px max-content 1px max-content minmax(180px, 1fr) max-content 4px',
				gridTemplateRows: 'auto auto',
				columnGap: 12,
				alignItems: 'center'
			},
			[narrow]: {
				display: 'flex',
				flexWrap: 'wrap',
				alignItems: 'center',
				gap: 12,
				padding: theme.spacing(1.5, 2)
			}
		},
		band: {
			[wide]: {
				gridRow: 1,
				gridColumn: '1 / -1',
				alignSelf: 'stretch',
				borderBottom: `1px solid ${line}`,
				backgroundColor: fade(theme.palette.background.paper, isDark ? 0.35 : 0.6)
			},
			[narrow]: {
				display: 'none'
			}
		},
		quotaCell: {
			position: 'relative',
			[wide]: {
				gridRow: 1,
				paddingTop: 10,
				paddingBottom: 10
			}
		},
		mainCell: {
			[wide]: {
				gridRow: 2,
				paddingTop: 12,
				paddingBottom: 12
			}
		},
		col2: { [wide]: { gridColumn: 2 } },
		col3: { [wide]: { gridColumn: 3, justifySelf: 'center' } },
		col4: { [wide]: { gridColumn: 4, justifySelf: 'start' } },
		col5: { [wide]: { gridColumn: 5 } },
		col6: { [wide]: { gridColumn: 6, justifySelf: 'end' } },
		centered: { [wide]: { justifySelf: 'center' } },
		heading: {
			display: 'flex',
			alignItems: 'center',
			gap: 12,
			minWidth: 0,
			[narrow]: {
				width: '100%'
			}
		},
		separator: {
			width: 1,
			height: 28,
			flex: 'none',
			backgroundColor: line,
			[narrow]: {
				display: 'none'
			}
		},
		iconTile: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: 44,
			height: 44,
			flex: 'none',
			borderRadius: 12,
			backgroundColor: theme.palette.primary.main,
			color: '#111',
			'& svg': { fontSize: 24 }
		},
		headingText: {
			display: 'flex',
			flexDirection: 'column',
			gap: 2,
			minWidth: 0
		},
		title: {
			fontSize: 20,
			fontWeight: 700,
			letterSpacing: '-0.01em',
			lineHeight: 1.2,
			whiteSpace: 'nowrap'
		},
		meta: {
			fontSize: 12,
			color: theme.palette.text.secondary,
			fontVariantNumeric: 'tabular-nums',
			whiteSpace: 'nowrap'
		},
		segment: {
			display: 'flex',
			alignItems: 'center',
			gap: 0,
			flex: 'none'
		},
		segmentOption: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			justifyContent: 'center',
			minWidth: 60,
			padding: theme.spacing(0.5, 1.5),
			borderRadius: 6,
			fontFamily: 'inherit',
			color: theme.palette.text.primary,
			transition: 'background-color 0.2s',
			'&:hover': {
				backgroundColor: theme.palette.action.hover
			},
			'&:not(:last-child)': {
				borderRight: `1px solid ${theme.palette.divider}`
			},
			'&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 }
		},
		segmentOn: {
			backgroundColor: theme.palette.action.selected
		},
		segmentCount: {
			fontWeight: 700,
			fontSize: '1.1rem',
			lineHeight: 1.15,
			fontVariantNumeric: 'tabular-nums',
			color: 'inherit'
		},
		segmentLabel: {
			fontSize: '0.65rem',
			fontWeight: 500,
			textTransform: 'uppercase',
			letterSpacing: '0.4px',
			color: theme.palette.text.secondary,
			lineHeight: 1.4
		},
		countActive: {
			color: isDark ? green[400] : green[800]
		},
		countInactive: {
			color: isDark ? red[300] : red[700]
		},
		search: {
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			height: 40,
			minWidth: 0,
			padding: '0 8px 0 12px',
			borderRadius: 10,
			boxSizing: 'border-box',
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.divider}`,
			transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color'], { duration: 180 }),
			'&:hover': { borderColor: fade(theme.palette.text.primary, 0.3) },
			'&:focus-within': {
				borderColor: theme.palette.primary.main,
				boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.45)}`
			},
			[narrow]: { flex: '1 1 100%' }
		},
		searchIcon: {
			color: theme.palette.text.secondary,
			fontSize: 18,
			flex: 'none'
		},
		searchInput: {
			flex: 1,
			fontSize: 14
		},
		clearButton: {
			padding: 4,
			borderRadius: 6
		},
		clearIcon: {
			fontSize: 16
		},
		actions: {
			display: 'flex',
			alignItems: 'center',
			gap: 8,
			flex: 'none',
			[narrow]: {
				width: '100%',
				justifyContent: 'flex-end'
			}
		},
		exportButton: {
			width: 40,
			height: 40,
			borderRadius: 10,
			border: `1px solid ${line}`,
			color: theme.palette.text.secondary,
			backgroundColor: theme.palette.background.paper,
			transition: theme.transitions.create(['background-color', 'color', 'border-color', 'transform'], { duration: 150 }),
			'&:hover': { backgroundColor: soft, color: theme.palette.text.primary, borderColor: fade(theme.palette.text.primary, 0.3) },
			'&:active': { transform: 'scale(0.94)' },
			'&.Mui-disabled': { opacity: 0.45 },
			'& .MuiIconButton-label svg': { display: 'block', fontSize: 20 }
		},
		createButton: {
			width: 40,
			height: 40,
			borderRadius: 10,
			color: '#111',
			backgroundColor: theme.palette.primary.main,
			transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], { duration: 150 }),
			'&:hover': {
				backgroundColor: theme.palette.primary.dark,
				boxShadow: `0 6px 16px ${fade(theme.palette.primary.main, 0.45)}`
			},
			'&:active': { transform: 'scale(0.94)' },
			'& .MuiIconButton-label svg': { display: 'block', fontSize: 22 }
		},
		quotaTitle: {
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: '0.08em',
			textTransform: 'uppercase',
			color: theme.palette.text.secondary,
			lineHeight: 1,
			whiteSpace: 'nowrap'
		},
		quotaDivider: {
			width: 1,
			height: 16,
			flex: 'none',
			backgroundColor: line,
			[narrow]: { display: 'none' }
		},
		quotaCount: {
			display: 'block',
			lineHeight: '20px',
			[wide]: { height: 40 },
			whiteSpace: 'nowrap',
			fontVariantNumeric: 'tabular-nums'
		},
		quotaNumber: {
			fontSize: 17,
			fontWeight: 700,
			transition: 'color 300ms'
		},
		quotaMax: {
			fontSize: 12,
			fontWeight: 500,
			marginLeft: 4,
			color: theme.palette.text.secondary
		},
		seatBar: {
			display: 'flex',
			alignItems: 'center',
			gap: 3,
			width: '100%',
			height: 8,
			[narrow]: { flex: '1 1 100%' }
		},
		seat: {
			flex: 1,
			height: 8,
			borderRadius: 4,
			backgroundColor: fade(theme.palette.text.primary, isDark ? 0.14 : 0.1),
			transition: 'background-color 420ms cubic-bezier(0.22, 1, 0.36, 1)',
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none'
			}
		},
		...Array.from({ length: SEGMENT_MAX }).reduce((acc, _, i) => {
			acc['seatDelay' + i] = { transitionDelay: (i * 35) + 'ms' };
			return acc;
		}, {}),
		track: {
			width: '100%',
			height: 8,
			borderRadius: 4,
			backgroundColor: fade(theme.palette.text.primary, isDark ? 0.14 : 0.1),
			[narrow]: { flex: '1 1 100%' }
		},
		trackBar: {
			borderRadius: 4,
			transition: 'transform 900ms cubic-bezier(0.22, 1, 0.36, 1), background-color 300ms',
			'@media (prefers-reduced-motion: reduce)': {
				transition: 'none'
			}
		},
		quotaStatus: {
			display: 'block',
			lineHeight: '20px',
			[wide]: { height: 40 },
			fontSize: 15,
			fontWeight: 700,
			fontVariantNumeric: 'tabular-nums',
			whiteSpace: 'nowrap',
			transition: 'color 300ms',
			[narrow]: { marginLeft: 'auto' }
		},
		toneNormal: {
			color: isDark ? green[400] : green[800]
		},
		toneNearCapacity: {
			color: isDark ? orange[400] : orange[900]
		},
		toneAtCapacity: {
			color: isDark ? red[300] : red[700]
		},
		fillNormal: {
			backgroundColor: isDark ? green[400] : green[600]
		},
		fillNearCapacity: {
			backgroundColor: isDark ? orange[400] : orange[700]
		},
		fillAtCapacity: {
			backgroundColor: isDark ? red[300] : red[600]
		}
	};
});

const TONE_CLASS = {
	normal: 'toneNormal',
	nearCapacity: 'toneNearCapacity',
	atCapacity: 'toneAtCapacity'
};

const FILL_CLASS = {
	normal: 'fillNormal',
	nearCapacity: 'fillNearCapacity',
	atCapacity: 'fillAtCapacity'
};

export default function UserPageHeader({
	language,
	shown,
	total,
	quota,
	quotaStatus,
	quotaPercentage,
	statusFilter,
	counts,
	onStatusFilterChange,
	searchValue,
	onSearchChange,
	onExport,
	exportDisabled,
	onCreate
}) {
	const classes = useStyles();
	const [mounted, setMounted] = useState(false);
	useEffect(() => {
		// first frame paints the empty bar, second frame starts the transition
		var outer = 0;
		var inner = 0;
		outer = requestAnimationFrame(() => {
			inner = requestAnimationFrame(() => setMounted(true));
		});
		return () => {
			cancelAnimationFrame(outer);
			cancelAnimationFrame(inner);
		};
	}, []);
	const activeCount = parseInt(quota.active_count, 10) || 0;
	const maxUsers = parseInt(quota.max_users, 10) || 0;
	const noLimit = maxUsers <= 0;
	const segmented = !noLimit && maxUsers <= SEGMENT_MAX;
	const seats = segmented ? Array.from({ length: maxUsers }, (_, i) => i) : [];
	const tone = noLimit ? 'normal' : quotaStatus;
	const toneClass = classes[TONE_CLASS[tone]];
	const fillClass = classes[FILL_CLASS[tone]];
	const statusLabel = noLimit ? language.no_limit : Math.round(quotaPercentage) + '%';

	const options = [
		{ key: 'all', label: language.total, countClass: null },
		{ key: 'active', label: language.active, countClass: classes.countActive },
		{ key: 'inactive', label: language.inactive, countClass: classes.countInactive }
	];

	return (
		<div className={classes.root} role="group" aria-label={language.user_account}>
			<div className={classes.band} aria-hidden="true" />

			<span className={clsx(classes.quotaCell, classes.col2, classes.quotaTitle)}>{language.active_user_quota}</span>
			<span className={clsx(classes.quotaCell, classes.col3, classes.quotaDivider)} aria-hidden="true" />
			<span className={clsx(classes.quotaCell, classes.col4, classes.centered, classes.quotaCount)} aria-label={language.active_user_quota}>
				<span className={clsx(classes.quotaNumber, toneClass)}>{activeCount}</span>
				{!noLimit && <span className={classes.quotaMax}>{'/ ' + maxUsers}</span>}
			</span>
			{noLimit ? (
				<span className={clsx(classes.quotaCell, classes.col5)} aria-hidden="true" />
			) : segmented ? (
				<div className={clsx(classes.quotaCell, classes.col5, classes.seatBar)} aria-hidden="true">
					{seats.map((i) => (
						<span
							key={'seat-' + i}
							className={clsx(classes.seat, classes['seatDelay' + i], mounted && i < activeCount && fillClass)}
						/>
					))}
				</div>
			) : (
				<div className={clsx(classes.quotaCell, classes.col5)}>
					<LinearProgress
						variant="determinate"
						value={mounted ? quotaPercentage : 0}
						classes={{ root: classes.track, bar: clsx(classes.trackBar, fillClass) }}
					/>
				</div>
			)}
			<span className={clsx(classes.quotaCell, classes.col6, classes.centered, classes.quotaStatus, toneClass)}>{statusLabel}</span>

			<div className={clsx(classes.mainCell, classes.col2, classes.heading)}>
				<span className={classes.iconTile} aria-hidden="true"><PeopleIcon /></span>
				<div className={classes.headingText}>
					<Typography component="h1" className={classes.title}>{language.user_account}</Typography>
					<Typography component="span" className={classes.meta}>
						{usersShownText(language.users_shown, shown, total)}
					</Typography>
				</div>
			</div>
			<span className={clsx(classes.mainCell, classes.col3, classes.separator)} aria-hidden="true" />

			<div className={clsx(classes.mainCell, classes.col4, classes.segment)} role="group" aria-label={language.status}>
				{options.map((option) => {
					const active = statusFilter === option.key;
					return (
						<ButtonBase
							key={option.key}
							className={clsx(classes.segmentOption, active && classes.segmentOn)}
							aria-pressed={active}
							onClick={() => onStatusFilterChange(option.key)}
						>
							<span className={clsx(classes.segmentCount, option.countClass)}>
								{counts[option.key] || 0}
							</span>
							<span className={classes.segmentLabel}>{option.label}</span>
						</ButtonBase>
					);
				})}
			</div>

			<label className={clsx(classes.mainCell, classes.col5, classes.search)}>
				<SearchIcon className={classes.searchIcon} />
				<InputBase
					className={classes.searchInput}
					placeholder={language.search}
					value={searchValue}
					onChange={(e) => onSearchChange(e.target.value)}
					inputProps={{ 'aria-label': language.search }}
				/>
				{searchValue !== '' && (
					<IconButton
						size="small"
						className={classes.clearButton}
						aria-label={language.clear}
						onClick={() => onSearchChange('')}
					>
						<CloseIcon className={classes.clearIcon} />
					</IconButton>
				)}
			</label>

			<div className={clsx(classes.mainCell, classes.col6, classes.actions)}>
				<Tooltip title={language.export}>
					<span>
						<IconButton
							className={classes.exportButton}
							aria-label={language.export}
							onClick={onExport}
							disabled={exportDisabled}
						>
							<GetAppIcon />
						</IconButton>
					</span>
				</Tooltip>
				<Tooltip title={language.create_user}>
					<IconButton
						className={classes.createButton}
						aria-label={language.create_user}
						onClick={onCreate}
					>
						<PersonAddIcon />
					</IconButton>
				</Tooltip>
			</div>
		</div>
	);
}
